import type {PromoBannerTreatment} from '@/config';
import type {UseServiceCenterBannerResult} from '@/modules/common/hooks/useServiceCenterBanner/useServiceCenterBanner';
import type {UseUpliftAdsReturn} from '@/modules/common/hooks/useUpliftAds/useUpliftAds';

import {useBreakpointDown, useBreakpointUp} from '@travel-frontend/core';
import {type CampaignBannerProps} from '@travel-frontend/ui-components';
import {noop} from 'lodash';
import {useEffect} from 'react';

import {CampaignBanner} from '@/components/CampaignBanner';
import {CashbackCampaignBanner} from '@/components/CashbackCampaignBanner';
import {CombinedCampaignBanner} from '@/components/CombinedCampaignBanner';
import {GenericBanner} from '@/components/GenericBanner';
import {SeasonalCashbackCampaignBanner} from '@/components/SeasonalCashbackCampaignBanner';
import {TourOperatorDiscountCampaignBanner} from '@/components/TourOperatorDiscountCampaignBanner';
import {BenefitBanner} from '@/modules/common/components/BenefitBanner';
import {NewsletterBanner} from '@/modules/common/components/NewsletterBanner';
import {ScarcityElement} from '@/modules/common/components/ScarcityElement';
import {ServiceBanner} from '@/modules/common/components/ServiceBanner';
import {useSeaCampaign} from '@/modules/common/context/SeaCampaign';
import {
  useCashbackCampaign,
  useCombinedCampaign,
  useLocalSplit,
  useTourOperatorDiscountCampaign,
} from '@/modules/common/hooks';
import {useNewsletterBannerSplit} from '@/modules/common/hooks/splits/useNewsletterBannerSplit';
import {
  useCampaignBanner,
  useGenericBanner,
} from '@/modules/common/hooks/useCampaigns';
import {useTracking} from '@/modules/common/hooks/useTracking';
import {UpliftAds} from '@/modules/hotels/components/List/components/UpliftAds/UpliftAds';
import {useAppSelector} from '@/redux/config';
import {getHotelCountFiltered} from '@/redux/v1/hotels/list/reducer';

type Props = {
  hotelListIndex: number;
  hotelListLength: number;
  forceSmallSizeForMapView?: boolean;
  firstInlineAd: UseUpliftAdsReturn;
  secondInlineAd: UseUpliftAdsReturn;
  topZoneAd: UseUpliftAdsReturn;
  serviceCenterBannerData: UseServiceCenterBannerResult;
};

const BENEFIT_BANNER_POSITION = 3;
const NEWSLETTER_BANNER_POSITION = 3;
const SERVICE_BANNER_POSITION = 1;
const P195_FIRST_INLINE_BANNER_POSITION = 2;
const P195_SECOND_INLINE_BANNER_POSITION = 4;
const CAMPAIGN_BANNER_POSITION_AFTER_EVERY = 7;
const SCARCITY_ELEMENT_POSITION = 1;

const handlePositionCampaignBanner = (index: number) =>
  (index + 1) % CAMPAIGN_BANNER_POSITION_AFTER_EVERY === 0;

export const Banners = ({
  hotelListIndex,
  hotelListLength,
  firstInlineAd,
  secondInlineAd,
  serviceCenterBannerData,
  forceSmallSizeForMapView,
  topZoneAd,
}: Props) => {
  const hotelCountFiltered = useAppSelector(getHotelCountFiltered);
  const {dispatchGenericTrackingEvent} = useTracking();
  const mdDown = useBreakpointDown('md');
  const mdUp = useBreakpointUp('md');
  const lgUp = useBreakpointUp('lg');
  const seaCampaign = useSeaCampaign();
  const newsletterBannerSplit = useNewsletterBannerSplit();
  const discountCampaign = useTourOperatorDiscountCampaign();
  const cashbackCampaign = useCashbackCampaign();
  const campaignBanner = useCampaignBanner();
  const genericBanner = useGenericBanner();
  const combinedCampaign = useCombinedCampaign();
  const {enabled: serviceCenterBannerEnabled, ...restServiceCenterBannerData} =
    serviceCenterBannerData;
  const handlePositionBanner = (index: number, pos: number): boolean => {
    return index === pos || (index === hotelListLength - 1 && index < pos);
  };

  const promoSplitTreatment = useLocalSplit<PromoBannerTreatment>(
    'FFP_6002_campaign_banners'
  ).treatment;

  useEffect(() => {
    if (
      !topZoneAd.shouldShowBanner &&
      cashbackCampaign.shouldShowBanner &&
      !discountCampaign.shouldShowBanner &&
      handlePositionCampaignBanner(hotelListIndex)
    ) {
      dispatchGenericTrackingEvent({
        event: 'promotion_banner',
        event_action: 'cashback_loaded',
        ad_position: `${hotelListIndex + 2}`,
        non_interaction: true,
      });
    }
  }, [
    cashbackCampaign.shouldShowBanner,
    discountCampaign.shouldShowBanner,
    dispatchGenericTrackingEvent,
    hotelListIndex,
    topZoneAd.shouldShowBanner,
  ]);

  let bgImage: CampaignBannerProps['backgroundImage'] = undefined;
  if (campaignBanner?.campaign?.backgroundImage) {
    bgImage = {
      url: campaignBanner.campaign.backgroundImage.sm,
    };

    if (mdUp) {
      bgImage.url = campaignBanner.campaign.backgroundImage.md;
    }
    if (lgUp) {
      bgImage.url = campaignBanner.campaign.backgroundImage.lg;
    }
  }

  let InlineBanner: any = null;

  if (
    !forceSmallSizeForMapView &&
    handlePositionCampaignBanner(hotelListIndex)
  ) {
    if (
      campaignBanner.shouldShowBanner &&
      (promoSplitTreatment === 'hotels_on_offers_on' ||
        promoSplitTreatment === 'hotels_on_offers_off' ||
        promoSplitTreatment === 'default')
    ) {
      InlineBanner = (
        <CampaignBanner
          onClick={noop}
          title={campaignBanner.campaign.title}
          subtitle={campaignBanner.campaign.subTitle}
          badgeText={campaignBanner.campaign.badgeText}
          preset={campaignBanner.campaign.preset}
          backgroundImage={bgImage}
          link={{
            href: campaignBanner.campaign.url,
            target: '_blank',
            rel: 'noopener noreferrer',
          }}
          legalHint={{
            text: campaignBanner.campaign.legalHint,
            tooltip: campaignBanner.campaign.legalTooltip,
          }}
          textColor={campaignBanner.campaign.textColor}
          position={hotelListIndex + 2}
        />
      );
    } else if (genericBanner.shouldShowBanner) {
      InlineBanner = (
        <GenericBanner
          conditionsId={genericBanner.campaign.conditionsId}
          backgroundImage={genericBanner.campaign.backgrounds}
          badgeImage={genericBanner.campaign.graphics}
          link={genericBanner.campaign.links}
          title={genericBanner.campaign.title}
          subtitle={genericBanner.campaign.subtitle}
        />
      );
    } else if (combinedCampaign.shouldShowBanner) {
      InlineBanner = (
        <CombinedCampaignBanner
          campaign={combinedCampaign.combinedCampaign}
          discountCampaign={combinedCampaign.discountCampaign}
          onClick={
            combinedCampaign.handleTourOperatorDiscountCampaignBannerClick
          }
        />
      );
    } else if (cashbackCampaign.shouldShowBanner) {
      InlineBanner = cashbackCampaign.campaign.seasonal ? (
        <SeasonalCashbackCampaignBanner
          {...cashbackCampaign.campaign}
          position={hotelListIndex + 2}
        />
      ) : (
        <CashbackCampaignBanner
          {...cashbackCampaign.campaign}
          position={hotelListIndex + 2}
        />
      );
    } else if (discountCampaign.shouldShowBanner) {
      InlineBanner = (
        <TourOperatorDiscountCampaignBanner
          {...discountCampaign.campaign}
          onClick={
            discountCampaign.handleTourOperatorDiscountCampaignBannerClick
          }
        />
      );
    }
  }

  return (
    <>
      {handlePositionBanner(hotelListIndex, SCARCITY_ELEMENT_POSITION) && (
        <ScarcityElement />
      )}
      {serviceCenterBannerEnabled &&
        handlePositionBanner(hotelListIndex, SERVICE_BANNER_POSITION) && (
          <ServiceBanner page="hotels" {...restServiceCenterBannerData} />
        )}
      {!forceSmallSizeForMapView &&
        firstInlineAd.shouldShowBanner &&
        handlePositionBanner(
          hotelListIndex,
          P195_FIRST_INLINE_BANNER_POSITION
        ) && (
          <UpliftAds
            category="inline"
            advertisements={firstInlineAd.advertisement}
          />
        )}
      {!forceSmallSizeForMapView &&
        (!cashbackCampaign.shouldShowBanner ||
          (discountCampaign.shouldShowBanner &&
            !cashbackCampaign.shouldShowBanner)) &&
        newsletterBannerSplit.enabled &&
        handlePositionBanner(hotelListIndex, NEWSLETTER_BANNER_POSITION) && (
          <NewsletterBanner treatment={newsletterBannerSplit.treatment} />
        )}
      {!forceSmallSizeForMapView &&
        secondInlineAd.shouldShowBanner &&
        (hotelCountFiltered >= P195_SECOND_INLINE_BANNER_POSITION ||
          !firstInlineAd.shouldShowBanner) &&
        handlePositionBanner(
          hotelListIndex,
          P195_SECOND_INLINE_BANNER_POSITION
        ) && (
          <UpliftAds
            category="inline"
            advertisements={secondInlineAd.advertisement}
          />
        )}
      {!forceSmallSizeForMapView &&
        seaCampaign.active &&
        seaCampaign.config &&
        handlePositionBanner(hotelListIndex, BENEFIT_BANNER_POSITION) && (
          <BenefitBanner
            headline={seaCampaign.config.benefits.headline}
            benefits={seaCampaign.config.benefits.items}
            short={mdDown}
            columns={2}
          />
        )}
      {InlineBanner}
    </>
  );
};
